/**
 * Pay an invoice.
 *
 *   details  invoice reference, amount, payer, and agreement to the terms
 *   card     Airwallex's card form, mounted on this page
 *   done     confirmation; the receipt email follows from the webhook
 *
 * Who the merchant is, the currency and the governing law are shown on both of
 * the first two steps, so they are in front of the payer before any card number
 * is entered — the card schemes' requirement, not a design choice.
 */

import { h, mount } from '../lib/dom.js';
import { CONTENT } from '../content.js';
import { COMPANY, PAYMENT_LIMITS, POLICIES_UPDATED } from '../company.js';
import { pathFor } from '../lib/router.js';
import { createIntent, mountCardForm, PaymentError } from '../lib/payments.js';

/** Mirrors api/lib/pay.js — the server is authoritative. */
const INVOICE_RE = /^[A-Za-z0-9][A-Za-z0-9\-/]{1,39}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const state = {
  step: 'details',
  values: { invoice: '', amount: '', name: '', email: '', agree: false },
  errors: {},
  formError: null,
  busy: false,
  intent: null,
  element: null,
  reference: null,
  renderedAt: Date.now(),
  trap: '',
};

export function renderPay(lang) {
  const host = h('section', { class: 'pay' });
  paint(host, lang);
  return host;
}

function paint(host, lang) {
  teardownCardForm();
  const t = CONTENT[lang].pay;
  const view = state.step === 'done' ? done(t) : state.step === 'card' ? cardStep(host, lang, t) : details(host, lang, t);
  mount(host, h('span', { class: 'kicker' }, t.kicker), h('h1', { class: 'pay-title' }, t.title), view);
  if (state.step === 'card') startCardForm(host, lang, t);
}

/** The merchant, the currency and the law — shown before any card field appears. */
function disclosure(lang, t) {
  const f = CONTENT[lang].footer;
  return h(
    'dl',
    { class: 'pay-disclosure' },
    h('dt', null, t.merchant),
    h('dd', null, `${COMPANY.legalName}, Hong Kong`, h('br'), `${f.br} ${COMPANY.brNumber || f.pending}`),
    h('dt', null, t.currency),
    h('dd', null, `${COMPANY.currency} — ${currencyName(lang)}`),
    h('dt', null, t.law),
    h('dd', null, t.lawValue),
  );
}

/** "Hong Kong dollar", "港元", "港币", "香港ドル" — named by the browser, in the reader's language. */
function currencyName(lang) {
  try {
    return new Intl.DisplayNames([CONTENT[lang].htmlLang], { type: 'currency' }).of(COMPANY.currency);
  } catch {
    return COMPANY.currency;
  }
}

function money(lang, amount) {
  return new Intl.NumberFormat(CONTENT[lang].htmlLang, { style: 'currency', currency: COMPANY.currency }).format(amount);
}

function details(host, lang, t) {
  const busy = state.busy;

  const field = (key, label, control) =>
    h(
      'div',
      { class: 'field' },
      h('label', { for: `pay-${key}` }, label),
      control,
      state.errors[key] && h('p', { class: 'field-error', id: `pay-${key}-error` }, state.errors[key]),
    );

  const bind = (key, extra = {}) => ({
    id: `pay-${key}`,
    class: 'input',
    value: state.values[key],
    disabled: busy,
    'aria-invalid': state.errors[key] ? 'true' : null,
    'aria-describedby': state.errors[key] ? `pay-${key}-error` : null,
    onInput: (e) => {
      state.values[key] = e.target.value;
    },
    ...extra,
  });

  // Policy links open in a new tab so reading them does not lose the form.
  const policy = (page, text) => h('a', { href: pathFor(lang, page), target: '_blank', rel: 'noopener' }, text);
  const [pre, termsText, mid, refundsText, post] = t.agree;

  return h(
    'div',
    { class: 'pay-grid' },
    h('div', null, h('p', { class: 'pay-intro' }, t.intro), disclosure(lang, t)),
    h(
      'form',
      {
        class: 'enquiry',
        novalidate: true,
        onSubmit: (e) => {
          e.preventDefault();
          submitDetails(host, lang, t);
        },
      },
      field('invoice', t.invoice, h('input', bind('invoice', { type: 'text', placeholder: t.invoicePh, maxlength: 40, autocomplete: 'off', spellcheck: 'false' }))),
      field('amount', t.amount, h('input', bind('amount', { type: 'text', inputmode: 'decimal', placeholder: '0.00', autocomplete: 'off' }))),
      h(
        'div',
        { class: 'row' },
        field('name', t.name, h('input', bind('name', { type: 'text', placeholder: t.namePh, maxlength: 120, autocomplete: 'name' }))),
        field('email', t.email, h('input', bind('email', { type: 'email', placeholder: 'you@example.com', maxlength: 254, autocomplete: 'email' }))),
      ),
      h(
        'div',
        { class: 'field' },
        h(
          'label',
          { class: 'pay-agree' },
          h('input', {
            id: 'pay-agree',
            type: 'checkbox',
            checked: state.values.agree,
            disabled: busy,
            'aria-invalid': state.errors.agree ? 'true' : null,
            onChange: (e) => {
              state.values.agree = e.target.checked;
            },
          }),
          h('span', null, pre, policy('terms', termsText), mid, policy('refunds', refundsText), post),
        ),
        state.errors.agree && h('p', { class: 'field-error' }, state.errors.agree),
      ),
      h(
        'div',
        { class: 'hp', 'aria-hidden': 'true' },
        h('label', { for: 'pay-website' }, 'Website'),
        h('input', { id: 'pay-website', type: 'text', tabindex: '-1', autocomplete: 'off', value: state.trap, onInput: (e) => (state.trap = e.target.value) }),
      ),
      h(
        'div',
        { class: 'submit-row' },
        h('button', { type: 'submit', class: 'btn btn-primary', disabled: busy }, busy ? t.preparing : t.cont),
        state.formError && h('p', { class: 'form-error', role: 'alert' }, state.formError),
      ),
    ),
  );
}

/** "1,234.5" → 1234.5; anything else → NaN. At most two decimal places. */
export function parseAmount(raw) {
  const cleaned = String(raw).replace(/[,\s]/g, '').replace(/^HK\$|^\$/i, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return NaN;
  return Number(cleaned);
}

function validate(t) {
  const v = state.values;
  const errors = {};
  if (!v.invoice.trim()) errors.invoice = t.errRequired;
  else if (!INVOICE_RE.test(v.invoice.trim())) errors.invoice = t.errInvoice;

  const amount = parseAmount(v.amount);
  if (!String(v.amount).trim()) errors.amount = t.errRequired;
  else if (!(amount >= PAYMENT_LIMITS.min && amount <= PAYMENT_LIMITS.max)) {
    errors.amount = t.errAmount
      .replace('{min}', PAYMENT_LIMITS.min.toLocaleString('en'))
      .replace('{max}', PAYMENT_LIMITS.max.toLocaleString('en'));
  }

  if (!v.name.trim()) errors.name = t.errRequired;
  if (!v.email.trim()) errors.email = t.errRequired;
  else if (!EMAIL_RE.test(v.email.trim())) errors.email = t.errEmail;
  if (!v.agree) errors.agree = t.errAgree;
  return errors;
}

async function submitDetails(host, lang, t) {
  state.errors = validate(t);
  state.formError = null;
  if (Object.keys(state.errors).length) {
    paint(host, lang);
    host.querySelector('[aria-invalid="true"]')?.focus();
    return;
  }

  state.busy = true;
  paint(host, lang);
  try {
    state.intent = await createIntent({
      invoiceRef: state.values.invoice.trim(),
      amount: parseAmount(state.values.amount),
      name: state.values.name.trim(),
      email: state.values.email.trim(),
      lang,
      agreed: true,
      termsVersion: POLICIES_UPDATED,
      website: state.trap,
      elapsedMs: Date.now() - state.renderedAt,
    });
    state.step = 'card';
  } catch (err) {
    const unavailable = err instanceof PaymentError && (err.status === 503 || err.code === 'payments_unavailable');
    state.formError = unavailable ? t.errUnavailable : t.errFailed;
    if (err instanceof PaymentError && err.field && t[`err${err.field[0].toUpperCase()}${err.field.slice(1)}`]) {
      state.errors[err.field] = t[`err${err.field[0].toUpperCase()}${err.field.slice(1)}`];
    }
  } finally {
    state.busy = false;
  }
  paint(host, lang);
}

function cardStep(host, lang, t) {
  const amount = state.intent?.amount ?? parseAmount(state.values.amount);
  return h(
    'div',
    { class: 'pay-grid' },
    h(
      'div',
      null,
      h('p', { class: 'pay-summary' }, `${t.summary} `, h('strong', null, money(lang, amount)), ` ${t.forInvoice} `, h('strong', null, state.values.invoice)),
      disclosure(lang, t),
      h(
        'p',
        null,
        h(
          'button',
          {
            type: 'button',
            class: 'btn btn-ghost',
            onClick: () => {
              state.step = 'details';
              state.intent = null;
              paint(host, lang);
            },
          },
          t.change,
        ),
      ),
    ),
    h(
      'div',
      null,
      h('div', { id: 'awx-dropin', class: 'pay-card' }),
      // 3-D Secure renders here, inside this page.
      h('div', { id: 'awx-3ds', class: 'pay-3ds' }),
      h('p', { class: 'pay-secure' }, t.secure),
      state.formError && h('p', { class: 'form-error', role: 'alert' }, state.formError),
    ),
  );
}

async function startCardForm(host, lang, t) {
  try {
    state.element = await mountCardForm({
      intent: state.intent,
      lang,
      container: 'awx-dropin',
      authContainer: 'awx-3ds',
      onSuccess: () => {
        state.reference = state.intent.intentId;
        state.step = 'done';
        paint(host, lang);
        host.scrollIntoView({ block: 'start', behavior: 'smooth' });
      },
      onError: (_detail, { attempted } = {}) => {
        const note = host.querySelector('.form-error') || host.querySelector('.pay-secure').insertAdjacentElement('afterend', h('p', { class: 'form-error', role: 'alert' }));
        // Before the payer has pressed Pay, a failure is ours or the payment
        // provider's — "try another card" would blame a card nobody entered.
        note.textContent = attempted ? t.errFailed : t.errUnavailable;
      },
    });
  } catch {
    state.formError = t.errLoad;
    const secure = host.querySelector('.pay-secure');
    secure?.insertAdjacentElement('afterend', h('p', { class: 'form-error', role: 'alert' }, t.errLoad));
  }
}

function teardownCardForm() {
  try {
    state.element?.destroy();
  } catch {
    /* already gone with the DOM it was mounted in */
  }
  state.element = null;
}

function done(t) {
  return h(
    'div',
    { class: 'sent-panel', role: 'status', 'aria-live': 'polite' },
    h('h3', null, t.successTitle),
    h('p', null, `${t.successBody} ${state.values.email}.`),
    h('p', null, `${t.reference}: `, h('code', null, state.reference)),
  );
}
